/**
 * **两个 Area 注册表** —— 从 `Playground.vue` 向下拆出来的一小块（约 50 行）。
 *
 * ## 为什么单独成文件（归属修正）
 *
 * 上一轮抽小地图手势时，我把 `flowStores` / `graphEls` 顺手放进了
 * `useMiniMapGesture.ts`，理由写的是「只有它和宿主的挂载回调在用」。
 * **那个理由不成立**：
 *
 * ```text
 *   flowStores 用在：拖动位置发布 / 拖完收殓 / 焦点 store /
 *                  Area 挂载 / 节点重测 / 连线的手势机   ← 7 处
 *   graphEls   用在：小地图查 svg / Area 挂载 / 卸载清理 / 焦点元素  ← 4 处
 * ```
 *
 * 小地图只是**其中一个**消费者。这两个 Map 的真身是
 * 「**Area id → 那个 Area 的图引擎实例 / DOM 根**」—— 图引擎接入层的基础设施。
 * 所以归到这里，小地图手势改为**依赖它**（`MiniMapGestureCtx` 多两个入参）。
 *
 * ⚠️ 这一条值得记：**「我抽 A 的时候顺手把 x 放进 A」是归属错位的高发点**。
 * 判据不是「谁先用到」，而是「谁在语义上拥有它」。
 *
 * ## 归属
 *
 * **向下**：`Playground.vue` → `composables/useGraphRegistries.ts`，
 * 由宿主创建、`useMiniMapGesture` 与 `useFlowPublishing` 依赖它。
 */
import type { FlowStoreApi } from '@/graph/flowStore'

export function useGraphRegistries() {
  /** Area id → 那份画布的 store（每条发布 / 命令都按 Area 找它） */
  const flowStores = new Map<string, FlowStoreApi>()
  /** Area id → 那份画布的 DOM 根（查小地图 svg / 量尺寸用） */
  const graphEls = new Map<string, HTMLElement>()

  /**
   * 挂上 / 摘掉某个 Area 的画布根元素。
   *
   * ⚠️ 必须**判等之后再写**：`ref` 回调在每次重渲染时都会重跑，
   * 无条件 `set` 会让下面依赖它的 computed 每帧都失效。
   */
  function bindGraphEl(areaId: string, el: unknown): void {
    const next = (el as HTMLElement | null) ?? null
    const prev = graphEls.get(areaId) ?? null
    if (prev === next) return
    if (next) graphEls.set(areaId, next)
    else graphEls.delete(areaId)
  }

  /** 某个 Area 的画布 DOM 根（没有就 null） */
  function elOf(areaId: string): HTMLElement | null {
    return graphEls.get(areaId) ?? null
  }

  /** 挂上某个 Area 的图引擎 store */
  function setFlowStore(areaId: string, store: FlowStoreApi): void {
    flowStores.set(areaId, store)
  }

  /**
   * 让**所有** Area 的节点重新测量。
   *
   * ## 什么时候需要它（以及为什么平时不需要）
   *
   * 画布现在**自己盯着自己的 DOM**（`MutationObserver` + `ResizeObserver`）：
   * 内部一有增删、或元素尺寸一变，就自己重排测量 —— 所以
   * `flowStore.value?.updateNodeInternals()` 那些调用**对正确性已不是必需**，
   * 它们是引擎时代留下的兜底（那时端口位置由引擎从 store 算，不叫它就不更新）。
   * `verify:canvas-dom` 有两条断言把这件事钉住：
   *
   * ```text
   *   ★★ 自测量：加了一行之后**没人叫它**，它也把新端口量出来了
   *   ★★ 第二个画布：**没人叫它**，它自己发现能测量了并补上端口表
   * ```
   *
   * ⚠️ 而**动作作用于整份文档**时必须走这个版本：换主题、换字号这类
   * 影响所有窗口外观的改动，每个窗口都得重量一次（`useMenus` 注入的就是它）。
   *
   * ⚠️ 宿主里那 20 多处 `flowStore.…updateNodeInternals()` 打的是
   * **焦点窗口**的 store —— 用户在另一个窗口操作时，那个窗口一次都收不到。
   * 这在引擎时代是真 bug（"另一个窗口的连线不在端口上"），现在有自测量兜着。
   * 所以**不要**为了它去改那 20 多处（改动面大、收益为零），
   * 也**不要**因为"看着多余"就删掉（它们是给别的画布实现留的契约面）。
   */
  function updateAllNodeInternals(nodeIds?: string[]): void {
    for (const store of flowStores.values()) store.updateNodeInternals?.(nodeIds)
  }

  return {
    flowStores,
    graphEls,
    bindGraphEl,
    elOf,
    setFlowStore,
    updateAllNodeInternals,
  }
}
